
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as entity from '../entity'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Response} from '../core/Response';
import {FieldModelResponse} from '../core/Response';
import {ModuleSystemInfo} from '../core/ModuleSystemInfo';
import {FieldModelModuleSystemInfo} from '../core/ModuleSystemInfo';

/**
 * ModuleSystemInfoResponse struct
 */
class ModuleSystemInfoResponse extends Response {
  /**
   * Initialize struct
   * @param {!Response=} parent
   * @param {!ModuleSystemInfo=} moduleSystemInfo
   * @constructor
   */
  constructor (parent = new Response(), moduleSystemInfo = new ModuleSystemInfo()) {
    super()
    super.copy(parent)
    this.ModuleSystemInfo = moduleSystemInfo
  }

  /**
   * Copy struct (shallow copy)
   * @this {!ModuleSystemInfoResponse}
   * @param {!ModuleSystemInfoResponse} other Other struct
   * @returns {!ModuleSystemInfoResponse} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.ModuleSystemInfo != null) {
      this.ModuleSystemInfo = ModuleSystemInfo.fromObject(other.ModuleSystemInfo)
    } else {
      this.ModuleSystemInfo = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!ModuleSystemInfoResponse}
   * @returns {!ModuleSystemInfoResponse} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new ModuleSystemInfoResponseModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new ModuleSystemInfoResponseModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!ModuleSystemInfoResponse}
   * @param {!ModuleSystemInfoResponse} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof ModuleSystemInfoResponse)) {
      throw new TypeError('Instance of ModuleSystemInfoResponse is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!ModuleSystemInfoResponse}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      ModuleSystemInfo: ((this.ModuleSystemInfo != null) ? this.ModuleSystemInfo : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return ModuleSystemInfoResponse.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!ModuleSystemInfoResponse} other Object value
   * @returns {!ModuleSystemInfoResponse} Created struct
   */
  static fromObject (other) {
    return new ModuleSystemInfoResponse().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!ModuleSystemInfoResponse}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return ModuleSystemInfoResponse.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!ModuleSystemInfoResponse}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 10024
  }
}

export { ModuleSystemInfoResponse };

/**
 * Fast Binary Encoding ModuleSystemInfoResponse field model
 */
class FieldModelModuleSystemInfoResponse extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelResponse(buffer, 4 + 4)
    this.ModuleSystemInfo = new FieldModelModuleSystemInfo(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
  }

  /**
   * Get the core.Response field model
   * @this {!FieldModelModuleSystemInfoResponse}
   * @returns {!FieldModelResponse} core.Response field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelModuleSystemInfoResponse}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelModuleSystemInfoResponse}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.ModuleSystemInfo.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelModuleSystemInfoResponse}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.ModuleSystemInfo.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelModuleSystemInfoResponse}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelModuleSystemInfoResponse.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelModuleSystemInfoResponse}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 10024
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelModuleSystemInfoResponse}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelModuleSystemInfoResponse}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.ModuleSystemInfo.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ModuleSystemInfo.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ModuleSystemInfo.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelModuleSystemInfoResponse}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelModuleSystemInfoResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelModuleSystemInfoResponse}
   * @param {!ModuleSystemInfoResponse} fbeValue Default value, defaults is new ModuleSystemInfoResponse()
   * @returns {!ModuleSystemInfoResponse} ModuleSystemInfoResponse value
   */
  get (fbeValue = new ModuleSystemInfoResponse()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelModuleSystemInfoResponse}
   * @param {!ModuleSystemInfoResponse} fbeValue ModuleSystemInfoResponse value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.ModuleSystemInfo.fbeSize) <= fbeStructSize) {
      fbeValue.ModuleSystemInfo = this.ModuleSystemInfo.get()
    } else {
      fbeValue.ModuleSystemInfo = new ModuleSystemInfo()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ModuleSystemInfo.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelModuleSystemInfoResponse}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelModuleSystemInfoResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelModuleSystemInfoResponse}
   * @param {!ModuleSystemInfoResponse} fbeValue ModuleSystemInfoResponse value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelModuleSystemInfoResponse}
   * @param {ModuleSystemInfoResponse} fbeValue ModuleSystemInfoResponse value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.ModuleSystemInfo.set(fbeValue.ModuleSystemInfo)
  }
}

export { FieldModelModuleSystemInfoResponse };

/**
 * Fast Binary Encoding ModuleSystemInfoResponse model
 */
class ModuleSystemInfoResponseModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelModuleSystemInfoResponse(this.buffer, 4)
  }

  /**
   * Get the ModuleSystemInfoResponse model
   * @this {!ModuleSystemInfoResponseModel}
   * @returns {!FieldModelModuleSystemInfoResponse} model ModuleSystemInfoResponse model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!ModuleSystemInfoResponseModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!ModuleSystemInfoResponseModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return ModuleSystemInfoResponseModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!ModuleSystemInfoResponseModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelModuleSystemInfoResponse.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!ModuleSystemInfoResponseModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!ModuleSystemInfoResponseModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!ModuleSystemInfoResponseModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!ModuleSystemInfoResponseModel}
   * @param {!ModuleSystemInfoResponse} value ModuleSystemInfoResponse value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!ModuleSystemInfoResponseModel}
   * @param {!ModuleSystemInfoResponse} value ModuleSystemInfoResponse value, defaults is new ModuleSystemInfoResponse()
   * @return {!object} Deserialized ModuleSystemInfoResponse value and its size
   */
  deserialize (value = new ModuleSystemInfoResponse()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new ModuleSystemInfoResponse(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new ModuleSystemInfoResponse(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!ModuleSystemInfoResponseModel}
   * @param {!number} prev Previous ModuleSystemInfoResponse model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { ModuleSystemInfoResponseModel };
